import {
  type ApiContext,
  ApiNotAuthorized,
  type ClaimPreview,
  type InvalidFormField,
  type RegisteredAccountRequired,
  type Store,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { errorText } from "../primitives/errors";
import type { StoreRepository } from "../repositories/store-repository";
import type { BackendApi } from "./api";

/** The one-time bearer an invitation email carries: 32 random bytes, base64url. */
export const CLAIM_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * One sentence for every way an invitation can fail.
 *
 * Deliberately undifferentiated: invalid, expired, revoked and already-used are
 * the same answer to whoever is holding the link, and telling them apart would
 * say whether a token was ever real.
 */
export function claimMessage(errors: readonly Error[], fallback: string): string {
  if (errors.some((e) => e instanceof ApiNotAuthorized)) {
    return "This invitation is invalid, expired, revoked, or already used.";
  }
  return errorText(errors) || fallback;
}

/** What the invitation is for, without consuming it. */
export function previewClaim(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  token: string;
}): AsyncResult<ClaimPreview, ApiNotAuthorized | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.shopClaims.preview(ctx, o.token));
}

/** Grants the shop to the signed-in merchant and makes it the selected one. */
export async function redeemClaim(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
  token: string;
}): AsyncResult<Store, ApiNotAuthorized | RegisteredAccountRequired | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.shopClaims.redeem(ctx, o.token));
  if (!r.ok) return r;
  o.storeRepository.upsert(r.value);
  o.storeRepository.select(r.value.id);
  return r;
}
